import {Box, Button, Flex, HStack, Text} from '@chakra-ui/react';
import {TablerPlayerPlay} from '../../icons';
import ModalFrame from '../common/ModalFrame';
import usePlannerStyles from '../planner/usePlannerStyles';
import type {UniversityVideo} from '../../services/universityService';
import UniversityPoster from './UniversityPoster';

// One-time welcome for users who signed in through the assisted /start page.
// They told us they're beginners, so we offer (never force) the Getting
// Started playlist before they explore.
const UniversityWelcomeModal = ({
  isOpen,
  playlistVideos,
  onStart,
  onSkip,
}: {
  isOpen: boolean;
  playlistVideos: UniversityVideo[];
  onStart: () => void;
  onSkip: (dismissMethod: 'skip_button' | 'close') => void;
}) => {
  const styles = usePlannerStyles();
  const totalMinutes = Math.max(
    1,
    Math.round(
      playlistVideos.reduce((sum, video) => sum + video.durationSec, 0) / 60,
    ),
  );
  const firstVideo = playlistVideos[0];

  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={() => onSkip('close')}
      isCentered
      size={{base: 'md', md: 'xl'}}
      overlayProps={{bg: 'blackAlpha.300', backdropFilter: 'blur(10px)'}}
      bodyProps={{pt: 10, pb: 2}}
      footer={
        <Flex w="100%" direction="column" gap={2}>
          <Button
            w="100%"
            borderRadius="lg"
            bg="opalite.600"
            color="white"
            fontWeight="medium"
            leftIcon={<TablerPlayerPlay size={16} aria-hidden="true" />}
            onClick={onStart}
            _hover={{bg: 'opalite.700'}}
            _active={{bg: 'opalite.700'}}
            _focusVisible={styles.focusOutlineStyles}
          >
            Start watching
          </Button>
          <Button
            w="100%"
            variant="ghost"
            borderRadius="lg"
            fontWeight="medium"
            color={styles.subtleText}
            onClick={() => onSkip('skip_button')}
            _hover={{bg: styles.hoverBg}}
            _focusVisible={styles.focusOutlineStyles}
          >
            Explore on my own
          </Button>
        </Flex>
      }
    >
      {firstVideo && (
        <Box
          position="relative"
          borderRadius="xl"
          overflow="hidden"
          sx={{aspectRatio: '16 / 9'}}
          mb={4}
          as="button"
          type="button"
          w="100%"
          aria-label="Start the Getting Started videos"
          onClick={onStart}
          _focusVisible={styles.focusOutlineStyles}
        >
          <UniversityPoster
            video={firstVideo}
            iconSize={24}
            position="absolute"
            inset={0}
          />
        </Box>
      )}
      <Text
        fontSize="xl"
        fontWeight="semibold"
        color={styles.textColor}
        lineHeight="1.3"
      >
        Welcome to Opalite
      </Text>
      <Text fontSize="sm" color={styles.subtleText} mt={2} lineHeight="1.6">
        {playlistVideos.length} short videos, about {totalMinutes} minutes
        total, will walk you from starting a visit to finishing your note. You
        can always find them later under{' '}
        <Text as="span" fontWeight="semibold">
          Opalite University
        </Text>{' '}
        in the sidebar.
      </Text>
      <HStack spacing={2} mt={3} flexWrap="wrap" rowGap={1}>
        {playlistVideos.map((video, index) => (
          <Text key={video.id} fontSize="xs" color={styles.metaTextColor}>
            {index + 1}. {video.title}
          </Text>
        ))}
      </HStack>
    </ModalFrame>
  );
};

export default UniversityWelcomeModal;
